import React, { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import ProfileTracking from "./ProfileTracking";
import Navbar from "./components/Navbar";
import Leaderboards from "./components/Leaderboards";
import SteamStatusUpdate from "./SteamStatusUpdate";

// Configure axios defaults
axios.defaults.withCredentials = true; // globally enable sending cookies
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Use same-origin /api in production (proxied by Render)
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? "/api"
    : "http://localhost:8000");

// Create axios instance with specific config
const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [, setSteamId] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Check auth status on mount
  useEffect(() => {
    let mounted = true;

    async function checkStatus() {
      try {
        console.log("Checking auth status...");
        console.log("Cookies:", document.cookie);
        console.log("API Base:", axiosInstance.defaults.baseURL);
        const res = await axiosInstance.get('/auth/status');
        console.log("Auth status response:", res.data);
        
        if (!mounted) {
          console.log("Component unmounted during status check");
          return;
        }

        if (res.data.logged_in) {
          console.log("User is logged in, steam_id:", res.data.steam_id);
          setLoggedIn(true);
          setSteamId(res.data.steam_id);
          // Set loading to false before fetching summary
          setLoading(false);
          // Fetch summary after showing the UI
          console.log("Fetching user summary...");
          await fetchUserSummary();
          console.log("User summary fetched");
        } else {
          console.log("User is not logged in");
          if (mounted) {
            setLoggedIn(false);
            setLoading(false);
          }
        }
      } catch (e) {
        console.error("Error checking auth status:", e);
        console.error("Error details:", e.response?.data);
        if (mounted) {
          setLoggedIn(false);
          setLoading(false);
        }
      }
    }
    
    checkStatus();

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch user summary after login
  async function fetchUserSummary() {
    try {
      const res = await axiosInstance.get('/user/summary');
      setUserSummary(res.data);
    } catch (e) {
      console.error("Failed to load steam data", e);
      setError("Failed to load Steam data.");
    }
  }

  // Open Steam login page
  function loginViaSteam() {
    setError(null);
    console.log("Redirecting browser to Steam login...");
  
    window.location.href = "/auth/steam/url";
  }

  // Logout user
  async function logout() {
    try {
      await axiosInstance.get('/auth/logout');
      setLoggedIn(false);
      setSteamId(null);
      setUserSummary(null);
      setError(null);
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  // Test Cookies Page Component
  function TestCookiesPage() {
    const [testResults, setTestResults] = useState({});
    const [steamIdInput, setSteamIdInput] = useState('');
    const [userSteamIdInput, setUserSteamIdInput] = useState('');

    const API_BASE = axiosInstance.defaults.baseURL;

    console.log("TestCookiesPage component rendered");
    console.log("API_BASE:", API_BASE);

    async function makeRequest(endpoint, options = {}) {
      try {
        console.log("Making request to:", `${API_BASE}${endpoint}`);
        const method = (options && options.method) || 'GET';
        const data = options && options.body ? JSON.parse(options.body) : undefined;
        const res = await axiosInstance.request({ url: endpoint, method, data });
        console.log("Request result:", res.data);
        return res.data;
      } catch (error) {
        console.error("Request error:", error);
        return { error: error.message, response: error.response?.data };
      }
    }

    async function setTestCookie() {
      const result = await makeRequest('/debug/set-test-cookie');
      setTestResults(prev => ({ ...prev, testCookie: result }));
    }

    async function checkTestCookie() {
      const cookies = document.cookie;
      const testCookie = cookies.split(';').find(c => c.trim().startsWith('test_cookie='));
      setTestResults(prev => ({ 
        ...prev, 
        testCookieCheck: {
          allCookies: cookies || 'No cookies',
          testCookieFound: !!testCookie,
          testCookieValue: testCookie ? testCookie.split('=')[1] : null
        }
      }));
    }

    async function checkEnvironment() {
      const result = await makeRequest('/debug/environment');
      setTestResults(prev => ({ ...prev, environment: result }));
    }

    async function debugSession() {
      const result = await makeRequest('/debug/session');
      setTestResults(prev => ({ ...prev, session: result }));
    }

    async function checkAuthStatus() {
      const result = await makeRequest('/auth/status');
      setTestResults(prev => ({ ...prev, auth: result }));
    }

    async function testSteamAPI() {
      if (!steamIdInput) {
        alert('Please enter a Steam ID');
        return;
      }
      const result = await makeRequest(`/debug/test-steam-api/${steamIdInput}`);
      setTestResults(prev => ({ ...prev, steamAPI: result }));
    }

    async function debugUser() {
      if (!userSteamIdInput) {
        alert('Please enter a Steam ID');
        return;
      }
      const result = await makeRequest(`/debug/user/${userSteamIdInput}`);
      setTestResults(prev => ({ ...prev, userDebug: result }));
    }

    return (
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <h1>VACS Cookie Test Page</h1>
        <p>This page helps debug cookie issues between frontend and backend.</p>
        <p><strong>Component loaded successfully!</strong> API Base: {API_BASE}</p>
        
        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#d1ecf1' }}>
          <h3>Environment Info</h3>
          <p><strong>Current URL:</strong> {window.location.href}</p>
          <p><strong>API Base:</strong> {API_BASE}</p>
          <p><strong>All Cookies:</strong> {document.cookie || 'No cookies'}</p>
        </div>

        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <h3>Test 1: Set Test Cookie</h3>
          <button onClick={setTestCookie} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Set Test Cookie</button>
          <button onClick={checkTestCookie} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Check Test Cookie</button>
          {testResults.testCookie && (
            <div style={{ backgroundColor: '#d4edda', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>Test Cookie Set</h4>
              <pre>{JSON.stringify(testResults.testCookie, null, 2)}</pre>
            </div>
          )}
          {testResults.testCookieCheck && (
            <div style={{ backgroundColor: '#d1ecf1', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>Test Cookie Check</h4>
              <p><strong>All cookies:</strong> {testResults.testCookieCheck.allCookies}</p>
              <p><strong>Test cookie found:</strong> {testResults.testCookieCheck.testCookieFound ? 'Yes' : 'No'}</p>
              {testResults.testCookieCheck.testCookieValue && (
                <p><strong>Test cookie value:</strong> {testResults.testCookieCheck.testCookieValue}</p>
              )}
            </div>
          )}
        </div>

        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <h3>Test 2: Environment Check</h3>
          <button onClick={checkEnvironment} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Check Environment</button>
          {testResults.environment && (
            <div style={{ backgroundColor: '#d1ecf1', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>Environment Configuration</h4>
              <pre>{JSON.stringify(testResults.environment, null, 2)}</pre>
            </div>
          )}
        </div>

        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <h3>Test 3: Session Debug</h3>
          <button onClick={debugSession} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Debug Session</button>
          {testResults.session && (
            <div style={{ backgroundColor: '#d1ecf1', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>Session Debug</h4>
              <pre>{JSON.stringify(testResults.session, null, 2)}</pre>
            </div>
          )}
        </div>

        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <h3>Test 4: Auth Status</h3>
          <button onClick={checkAuthStatus} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Check Auth Status</button>
          {testResults.auth && (
            <div style={{ backgroundColor: testResults.auth.logged_in ? '#d4edda' : '#f8d7da', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>Auth Status</h4>
              <pre>{JSON.stringify(testResults.auth, null, 2)}</pre>
            </div>
          )}
        </div>

        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <h3>Test 5: Steam API Test</h3>
          <input 
            type="text" 
            value={steamIdInput}
            onChange={(e) => setSteamIdInput(e.target.value)}
            placeholder="Enter Steam ID" 
            style={{ padding: '5px', margin: '5px' }}
          />
          <button onClick={testSteamAPI} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Test Steam API</button>
          {testResults.steamAPI && (
            <div style={{ backgroundColor: '#d1ecf1', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>Steam API Test Result</h4>
              <pre>{JSON.stringify(testResults.steamAPI, null, 2)}</pre>
            </div>
          )}
        </div>

        <div style={{ margin: '20px 0', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <h3>Test 6: User Debug</h3>
          <input 
            type="text" 
            value={userSteamIdInput}
            onChange={(e) => setUserSteamIdInput(e.target.value)}
            placeholder="Enter Steam ID" 
            style={{ padding: '5px', margin: '5px' }}
          />
          <button onClick={debugUser} style={{ padding: '10px 15px', margin: '5px', cursor: 'pointer' }}>Debug User</button>
          {testResults.userDebug && (
            <div style={{ backgroundColor: '#d1ecf1', padding: '10px', margin: '10px 0', borderRadius: '3px' }}>
              <h4>User Debug Result</h4>
              <pre>{JSON.stringify(testResults.userDebug, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Component to handle Steam redirect and validate login
  function SteamRedirect() {
    const navigate = useNavigate();
    const [validating, setValidating] = useState(true);
    const [loginError, setLoginError] = useState(null);

    useEffect(() => {
      let mounted = true; // Track if component is mounted

      console.log("Steam redirect component mounted");
      console.log("Current URL:", window.location.href);
      console.log("Search params:", window.location.search);
      
      async function doSuccessFlow(steamId) {
        // Keep spinner visible while finalizing
        setLoginError(null);
        setLoggedIn(true);
        setSteamId(steamId);
        console.log("Waiting for cookie to be set...");
        await new Promise(resolve => setTimeout(resolve, 600));
        try {
          console.log("Testing session access...");
          const sessionTest = await axiosInstance.get('/debug/session');
          console.log("Session test result:", sessionTest.data);
        } catch (e) {
          console.error("Session test failed:", e);
        }
        try {
          console.log("Fetching user summary");
          await fetchUserSummary();
        } catch (e) {
          console.error("Failed to fetch user summary:", e);
        }
        console.log("Navigating to home");
        navigate("/", { replace: true });
      }

      async function attemptValidate(openid_params, retries = 1) {
        try {
          console.log("Making request to:", axiosInstance.defaults.baseURL + '/auth/steam/validate');
          const res = await axiosInstance.post('/auth/steam/validate', { openid_params });
          console.log("Validation response:", res.data);
          if (!mounted) return;
          if (res.data.logged_in) {
            await doSuccessFlow(res.data.steam_id);
            return true;
          }
        } catch (e) {
          if (!mounted) return false;
          console.error("Validation error (attempt):", e);
        }
        if (retries > 0) {
          console.log("Retrying validation once after 700ms...");
          await new Promise(r => setTimeout(r, 700));
          return attemptValidate(openid_params, retries - 1);
        }
        return false;
      }

      async function validateLogin() {
        const params = new URLSearchParams(window.location.search);
        const openid_params = {};
        for (const [key, value] of params.entries()) {
          openid_params[key] = value;
        }
        console.log("OpenID params:", openid_params);
        const ok = await attemptValidate(openid_params, 1);
        if (!mounted) return;
        if (!ok) {
          setLoginError("Steam login failed. Please try again.");
          setValidating(false);
        }
      }

      validateLogin();

      // Cleanup function to prevent state updates after unmount
      return () => {
        mounted = false;
      };
    }, [navigate]);

    if (validating) {
      return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <p>Verifying Steam login...</p>
          <p>Please wait while we complete your login.</p>
        </div>
      );
    }

    if (loginError) {
      return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <p style={{ color: "red" }}>{loginError}</p>
          <button 
            onClick={() => navigate("/")}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Return to Home
          </button>
        </div>
      );
    }

    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <p>Login successful! Redirecting...</p>
      </div>
    );
  }

  // Login page component
  function LoginPage() {
    const navigate = useNavigate();
    const [devMode, setDevMode] = useState(false);
    const [testUsers, setTestUsers] = useState([]);
    const [checkingDevMode, setCheckingDevMode] = useState(true);
    // const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
      async function checkDevMode() {
        console.log("Checking dev mode...");
        try {
          const res = await axiosInstance.get('/auth/steam/url');
          console.log("Dev mode check response:", res.data);
          if (res.data.dev_mode) {
            console.log("Dev mode is enabled, setting test users:", res.data.test_users);
            setDevMode(true);
            setTestUsers(res.data.test_users);
          } else {
            console.log("Dev mode is disabled");
          }
        } catch (e) {
          console.error("Failed to check dev mode:", e);
        } finally {
          setCheckingDevMode(false);
        }
      }
      checkDevMode();
    }, []);

    async function loginAsTestUser(steamId) {
      console.log("Attempting dev login with steam_id:", steamId);
      try {
        const res = await axiosInstance.post('/auth/dev-login', { steam_id: steamId });
        console.log("Dev login response:", res.data);
        if (res.data.logged_in) {
          console.log("Dev login successful, redirecting to home");
          window.location.href = '/';
        } else {
          console.error("Dev login response indicates not logged in");
          setError("Login response indicates failure");
        }
      } catch (e) {
        console.error("Dev login failed:", e);
        console.error("Error details:", {
          message: e.message,
          response: e.response?.data,
          status: e.response?.status
        });
        setError("Failed to login as test user. Check console for details.");
      }
    }

    if (checkingDevMode) {
      return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <p>Loading login options...</p>
        </div>
      );
    }

    if (devMode) {
      console.log("Rendering dev mode login page with test users:", testUsers);
      return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <h2>Development Mode</h2>
          <p>Select a test user to login as:</p>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
            {testUsers.map(user => (
              <button
                key={user.steam_id}
                onClick={() => loginAsTestUser(user.steam_id)}
                style={{
                  padding: "10px 20px",
                  margin: "5px",
                  cursor: "pointer",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  width: "200px"
                }}
              >
                Login as {user.nickname}
              </button>
            ))}
          </div>
          <div style={{ marginTop: "20px" }}>
            <button 
              onClick={() => {
                console.log("Attempting real Steam login from dev mode");
                const steamLoginUrl = "https://steamcommunity.com/openid/login" +
                  "?openid.ns=http://specs.openid.net/auth/2.0" +
                  "&openid.mode=checkid_setup" +
                  `&openid.return_to=${window.location.origin}/steam-redirect` +
                  `&openid.realm=${window.location.origin}` +
                  "&openid.identity=http://specs.openid.net/auth/2.0/identifier_select" +
                  "&openid.claimed_id=http://specs.openid.net/auth/2.0/identifier_select";
                console.log("Generated Steam login URL:", steamLoginUrl);
                window.location.href = steamLoginUrl;
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Real Steam Login
            </button>
          </div>
          {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
        </div>
      );
    }

    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <p>You need to Log In via Steam.</p>
        <button onClick={loginViaSteam}>Log In via Steam</button>
        <div style={{ marginTop: "20px" }}>
          <button 
            onClick={() => {
              console.log("Navigating to test page");
              navigate("/test_cookies.html");
            }}
            style={{ 
              background: "none", 
              border: "none", 
              color: "#2196F3", 
              textDecoration: "underline", 
              cursor: "pointer",
              fontSize: "14px",
              marginRight: "10px"
            }}
          >
            Debug: Test Cookies Page
          </button>
          <button 
            onClick={() => {
              console.log("Navigating to debug page");
              navigate("/debug");
            }}
            style={{ 
              background: "none", 
              border: "none", 
              color: "#2196F3", 
              textDecoration: "underline", 
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Debug: Simple Test
          </button>
        </div>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </div>
    );
  }

  // Dashboard component
  function Dashboard() {
    const navigate = useNavigate();
    const [userStats, setUserStats] = useState(null);
    const [globalStats, setGlobalStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      async function fetchStats() {
        try {
          const [userStatsRes, globalStatsRes, trackedProfilesRes] = await Promise.all([
            axiosInstance.get('/analytics/user'),
            axiosInstance.get('/profiles/global'),
            axiosInstance.get('/profiles/tracked') // Fetch detailed tracked profiles
          ]);
          setUserStats(userStatsRes.data);
          setGlobalStats(globalStatsRes.data);
          // Store the profiles list directly in userStats, or in a new state if preferred
          setUserStats(prevStats => ({
            ...prevStats,
            profiles: userStatsRes.data.profiles_added > 0 ? trackedProfilesRes.data.profiles : []
          }));
        } catch (error) {
          console.error('Failed to fetch stats:', error);
        } finally {
          setLoading(false);
        }
      }

      fetchStats();
    }, []);

    if (loading) {
      return <div style={{ textAlign: "center", marginTop: "50px" }}>Loading stats...</div>;
    }

    // Common styles
    const containerStyle = {
      maxWidth: "800px",
      margin: "30px auto",
      padding: "20px",
      fontFamily: "Arial, sans-serif"
    };

    const headerStyle = {
      display: "flex",
      alignItems: "center",
      gap: "20px",
      marginBottom: "40px"
    };

    const contentBlockStyle = {
      backgroundColor: "#f5f5f5",
      padding: "25px",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      textAlign: "center",
      lineHeight: "1.6"
    };

    const buttonStyle = {
      padding: "12px 24px",
      backgroundColor: "#2196f3",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "16px",
      marginTop: "20px",
      transition: "background-color 0.2s"
    };

    const linkStyle = {
      color: "#2196f3",
      textDecoration: "none",
      fontWeight: "bold",
      cursor: "pointer"
    };

    // Determine which content to show based on user stats
    const hasNoTrackedAccounts = !userStats?.profiles_added || userStats.profiles_added === 0;

    return (
      <div style={containerStyle}>
        {/* Header with Avatar */}
        <div style={headerStyle}>
          <img
            src={userSummary?.avatar}
            alt="Avatar"
            style={{ 
              borderRadius: "50%", 
              width: "100px", 
              height: "100px",
              border: "3px solid #2196f3"
            }}
          />
          <h2 style={{ margin: 0 }}>Welcome, {userSummary?.nickname}</h2>
        </div>

        {/* Content Block */}
        <div style={contentBlockStyle}>
          {hasNoTrackedAccounts ? (
            // Content for users with no tracked accounts
            <p style={{ fontSize: "16px", margin: "0" }}>
              You have successfully logged in via Steam.{" "}
              <span 
                onClick={() => navigate("/tracked", { state: { openAddDialog: true }})}
                style={linkStyle}
              >
                Click here
              </span>
              {" "}to add your first suspect Steam account or{" "}
              <span
                onClick={() => navigate("/leaderboards")}
                style={linkStyle}
              >
                check out our Leaderboards
              </span>.
            </p>
          ) : (
            // Content for users with tracked accounts
            <>
              <p style={{ fontSize: "16px", margin: "0 0 15px 0" }}>
                You are currently following <strong>{userStats.profiles_added}</strong> suspects;{" "}
                <strong>{userStats.profiles_banned}</strong> of them have been banned.
              </p>
              <p style={{ fontSize: "16px", margin: "0" }}>
                Overall, <strong>{globalStats.total_count}</strong> suspects have been followed by all users;{" "}
                <strong>{globalStats.banned_count}</strong> have been banned.
              </p>
              {/* Display individual ban statuses if available in userStats (from /profiles/tracked) */}
              {userStats && userStats.profiles && userStats.profiles.length > 0 && (
                <div style={{ marginTop: "20px", textAlign: "left", borderTop: "1px solid #eee", paddingTop: "20px" }}>
                  <h3>Your Tracked Profiles Summary:</h3>
                  {userStats.profiles.map(profile => (
                    <div key={profile.steam_id} style={{ marginBottom: "10px", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }}>
                      <p><strong>Nickname:</strong> {profile.nickname} ({profile.steam_id})</p>
                      <p><strong>Status:</strong> 
                        <span style={{
                          color: profile.current_status === "Banned" ? "red" : "green",
                          fontWeight: "bold"
                        }}>
                          {profile.current_status}
                        </span>
                      </p>
                      {profile.vac_banned && <p><strong>VAC Banned:</strong> Yes {profile.vac_ban_date ? `(since ${new Date(profile.vac_ban_date).toLocaleDateString()})` : ""}</p>}
                      {profile.game_banned && <p><strong>Game Banned:</strong> Yes {profile.game_ban_date ? `(since ${new Date(profile.game_ban_date).toLocaleDateString()})` : ""}</p>}
                      {profile.total_bans > 0 && <p><strong>Total Bans:</strong> {profile.total_bans}</p>}
                      {profile.days_since_last_ban !== null && <p><strong>Days Since Last Ban:</strong> {profile.days_since_last_ban}</p>}
                      <p><a href={`https://steamcommunity.com/profiles/${profile.steam_id}`} target="_blank" rel="noopener noreferrer">View Profile on Steam</a></p>
                    </div>
                  ))}
                </div>
              )}
              <button 
                onClick={() => navigate("/tracked")}
                style={buttonStyle}
                onMouseOver={e => e.target.style.backgroundColor = "#1976d2"}
                onMouseOut={e => e.target.style.backgroundColor = "#2196f3"}
              >
                Go to Tracking
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Determine what to render based on state
  let content;
  
  console.log("=== Routing Debug ===");
  console.log("Current pathname:", location.pathname);
  console.log("Loading:", loading);
  console.log("Logged in:", loggedIn);
  console.log("Full location:", location);
  
  if (loading) {
    console.log("Rendering loading state");
    content = (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <p>Loading your profile...</p>
      </div>
    );
  } else if (location.pathname === "/steam-redirect") {
    console.log("Rendering Steam redirect component");
    console.log("Current location:", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      full: window.location.href
    });
    content = <SteamRedirect />;
  } else if (location.pathname === "/test_cookies.html" || location.pathname === "/debug") {
    console.log("Rendering test cookies page");
    // Always show test page regardless of login status
    content = (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Test Page Working!</h1>
        <p>If you can see this, the routing is working correctly.</p>
        <p>Current pathname: {location.pathname}</p>
        <button onClick={() => window.history.back()}>Go Back</button>
        <hr style={{ margin: '20px 0' }} />
        <TestCookiesPage />
      </div>
    );
  } else if (!loggedIn) {
    console.log("Rendering login page");
    content = <LoginPage />;
  } else {
    console.log("Rendering main app with navbar");
    content = (
      <div>
        <Navbar userSummary={userSummary} onLogout={logout} />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/tracked" element={<ProfileTracking />} />
          <Route path="/leaderboards" element={<Leaderboards />} />
          <Route path="/steam-status-update" element={<SteamStatusUpdate />} />
        </Routes>
      </div>
    );
  }
  
  console.log("Final content type:", typeof content);
  console.log("Content component:", content?.type?.name || "Unknown");

  return content;
}

export default App;
